import { CSSProperties } from "react";
import { motion } from "~/utils";
import {
  AgentEffectCommands,
  AgentEffectDescription,
  AgentIcon,
  Card,
  cardsDefRepository,
} from "~/business";
import { Duration, FontSize, Sizes, Spacing, transition } from "~/jsx/theme";
import { Colors } from "~/jsx/theme/Colors";
import { partition, Proposition } from "~/utils";
import { XAgentEffect } from "./XAgentEffect";
import { XAgentIcon } from "./XAgentIcon";
import { XCardFactionName } from "./XCardFactionName";
import { XResourceWithAmount } from "./XResourceWithAmount";
import { XTypography } from "./XTypography";
import { XArrowSvg, XDiscardSvg, XMeapleSvg, XTrashCardSvg } from "./svg";

export const XCard = ({
  card,
  width,
  style,
  onClick,
  isSelected,
  removeAnimationId = false,
}: {
  card: Card;
  width: number;
  onClick?: () => void;
  style?: CSSProperties;
  isSelected?: boolean;
  removeAnimationId?: boolean;
}) => {
  const isSelectable = Boolean(onClick);
  const cardProps = cardsDefRepository.getDef(card);
  const {
    name,
    agentIcons,
    cost,
    revealEffects,
    playEffects,
    factions,
    buyEffects = [],
    hasUnload = false,
    hasInfiltration = false,
  } = cardProps;
  const { ratio, radius } = Sizes.card;
  const height = width * ratio;
  const agentIconSize = agentIcons.length >= 6 ? 22 : 28;

  return (
    <motion.div
      {...(removeAnimationId ? {} : { layoutId: card.id })}
      onClick={onClick}
      style={{
        width,
        height,
        cursor: isSelectable ? "pointer" : "default",
      }}
    >
      <div
        style={{
          width: 274,
          height: 377,
          border: "solid 6px black",
          borderRadius: radius,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "lightgrey",
          overflow: "hidden",
          position: "relative",
          transform: `scale(${width / 274})`,
          transformOrigin: "top left",
          transition: transition({ durationInMs: Duration.Short }),
          boxShadow: isSelectable
            ? `${Colors.selection.selectable} 0px 0px 20px 7px`
            : isSelected
            ? `${Colors.selection.selected} 0px 0px 20px 7px`
            : "4px 4px 4px rgba(0, 0, 0, 0.33)",
          ...style,
        }}
      >
        <XTypography
          style={{
            backgroundColor: "white",
            textTransform: "uppercase",
            padding: Spacing.px4,
            fontSize: FontSize.px16,
          }}
        >
          {name}
        </XTypography>
        <div style={{ flex: 1, position: "relative" }}>
          <img
            src={`/image/card/${name}.jpg`}
            style={{
              width: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "absolute" }}>
            {factions.map((faction) => (
              <XCardFactionName key={faction} faction={faction} />
            ))}
          </div>
        </div>
        {cost ? (
          <div
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <XResourceWithAmount width={48} amount={cost} type="influence" />
            {buyEffects.map((effect, index) => (
              <XAgentEffect
                key={index}
                effectDescription={effect}
                width={36}
                context="card"
              />
            ))}
          </div>
        ) : null}
        <div style={{ marginBottom: Spacing.px8 }}>
          {agentIcons.length === Object.values(AgentIcon).length ? (
            <XActionContainer>
              <XTypography
                typographyStyle={{
                  color: Colors.common.white,
                  fontSize: FontSize.px20,
                }}
              >
                All slots
              </XTypography>
            </XActionContainer>
          ) : (
            agentIcons.map((agentIcon) => (
              <XActionContainer key={agentIcon}>
                {hasInfiltration && (
                  <XMeapleSvg
                    width={agentIconSize}
                    style={{ marginRight: Spacing.px4 }}
                  />
                )}
                <XAgentIcon agentIcon={agentIcon} width={agentIconSize} />
              </XActionContainer>
            ))
          )}
        </div>
        <div
          style={{
            background:
              "linear-gradient(180deg, #9E9893 0%, #A19589 19.79%, #A39385 50%, #A29489 79.17%, #9E9893 100%)",
            minHeight: "50px",
            borderTopLeftRadius: "4px",
            borderTopRightRadius: "4px",
            boxShadow: "0px -2px 2px rgba(0, 0, 0, 0.3)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: Spacing.px8,
            gap: Spacing.px8,
          }}
        >
          {groupSimpleEffects(playEffects).map((effect, index) => (
            <XAgentEffect
              key={index}
              effectDescription={effect}
              width={48}
              context="card"
            />
          ))}
        </div>
        <div
          style={{
            width: "100%",
            backgroundColor: "#282548",
            display: "flex",
            alignItems: "center",
            minHeight: "50px",
          }}
        >
          {hasUnload && (
            <div
              style={{
                display: "flex",
                gap: Spacing.px4,
                alignItems: "center",
                backgroundColor: "#876635",
                padding: Spacing.px8,
                height: "100%",
              }}
            >
              <XDiscardSvg width={40} />
              <XTrashCardSvg width={40} />
            </div>
          )}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: Spacing.px8,
              gap: Spacing.px8,
            }}
          >
            {groupSimpleEffects(revealEffects).map((effect, index) => (
              <XAgentEffect
                key={index}
                effectDescription={effect}
                width={48}
                context="card"
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const XActionContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      style={{
        background:
          "linear-gradient(180deg, #9E9893 0%, #A19589 19.79%, #A39385 50%, #A29489 79.17%, #9E9893 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "fit-content",
        minHeight: "20px",
        padding: "2px 8px",
        margin: "4px 0px",
        borderTopRightRadius: "4px",
        borderBottomRightRadius: "4px",
        boxShadow: "1px 2px 2px rgba(0, 0, 0, 0.3)",
      }}
    >
      {children}
    </div>
  );
};

const groupSimpleEffects = (
  effects: AgentEffectDescription[]
): AgentEffectDescription[] => {
  const { passing: implications, notPassing: simpleEffects } = partition(
    effects,
    (effect) => Proposition.isImplicationProposition(effect)
  );
  if (simpleEffects.length) {
    return [AgentEffectCommands.and(simpleEffects), ...implications];
  } else {
    return implications;
  }
};
